import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Check, CircleNotch, Warning, ArrowsClockwise, Link as LinkIcon } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pre-launch affiliate ops tab.
 *
 * Two sections:
 *  1. Sovrn Commerce global fallback — paste Site ID + API key once,
 *     toggle active so unaffiliated outbound links auto-monetise.
 *  2. Pending approvals — stores you've applied to via a network (currently
 *     just Commission Factory) but haven't received the advertiser_id for.
 *     One input + click to approve = store flips visible and starts earning.
 */
export default function NetworksTab() {
  const [sovrn, setSovrn] = useState(null);
  const [sovrnForm, setSovrnForm] = useState({ site_id: '', api_key: '', active: false });
  const [sovrnSaving, setSovrnSaving] = useState(false);
  const [sovrnMsg, setSovrnMsg] = useState(null);

  const [pending, setPending] = useState([]);
  const [advInputs, setAdvInputs] = useState({});  // {store_id: advertiser_id}
  const [approving, setApproving] = useState({});  // {store_id: bool}
  const [loadingPending, setLoadingPending] = useState(true);

  const loadSovrn = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/affiliate-global/sovrn`, { withCredentials: true });
      setSovrn(data);
      setSovrnForm({
        site_id: data?.site_id || '',
        api_key: '',
        active: !!data?.active,
      });
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadPending = useCallback(async () => {
    setLoadingPending(true);
    try {
      const { data } = await axios.get(`${API}/admin/affiliate-pending`, { withCredentials: true });
      setPending(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPending(false);
    }
  }, []);

  useEffect(() => {
    loadSovrn();
    loadPending();
  }, [loadSovrn, loadPending]);

  const saveSovrn = async () => {
    setSovrnSaving(true);
    setSovrnMsg(null);
    try {
      const body = { site_id: sovrnForm.site_id.trim(), active: sovrnForm.active };
      // Only send api_key if user typed something — empty means "don't change"
      if (sovrnForm.api_key) body.api_key = sovrnForm.api_key.trim();
      await axios.post(`${API}/admin/affiliate-global/sovrn`, body, { withCredentials: true });
      setSovrnMsg({ type: 'success', text: 'Sovrn config saved.' });
      setSovrnForm({ ...sovrnForm, api_key: '' });
      await loadSovrn();
    } catch (e) {
      setSovrnMsg({ type: 'error', text: e.response?.data?.detail || 'Save failed' });
    } finally {
      setSovrnSaving(false);
    }
  };

  const approve = async (storeId) => {
    const advId = (advInputs[storeId] || '').trim();
    if (!advId) {
      alert('Paste the advertiser ID from Commission Factory first.');
      return;
    }
    setApproving({ ...approving, [storeId]: true });
    try {
      await axios.post(`${API}/admin/affiliate-pending/approve`, {
        store_id: storeId,
        advertiser_id: advId,
      }, { withCredentials: true });
      // Optimistic: remove from local list
      setPending(pending.filter(p => p.store_id !== storeId));
      setAdvInputs({ ...advInputs, [storeId]: '' });
    } catch (e) {
      alert(e.response?.data?.detail || 'Approval failed');
    } finally {
      setApproving({ ...approving, [storeId]: false });
    }
  };

  return (
    <div className="space-y-10" data-testid="networks-tab">
      {/* ============ Sovrn Commerce ============ */}
      <div className="border-2 border-[#0A0A0A] bg-white p-6" data-testid="sovrn-section">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-black uppercase tracking-tight">Sovrn Commerce</h2>
          {sovrn?.configured ? (
            <span className={`text-xs font-bold uppercase tracking-wide px-2 py-1 ${sovrn.active ? 'bg-[#10B981] text-white' : 'bg-[#E4E4E7] text-[#52525B]'}`}>
              {sovrn.active ? 'Active' : 'Configured (inactive)'}
            </span>
          ) : (
            <span className="text-xs font-bold uppercase tracking-wide px-2 py-1 bg-[#FEF3C7] text-[#92400E]">
              Not configured
            </span>
          )}
        </div>
        <p className="text-sm text-[#52525B] mb-5">
          Paste your Sovrn Site ID + API key. Once active, any outbound link
          that isn't already wrapped by Amazon / CJ / Awin / Commission Factory
          will auto-monetise via Sovrn.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1">Site ID</label>
            <input
              type="text"
              value={sovrnForm.site_id}
              onChange={(e) => setSovrnForm({ ...sovrnForm, site_id: e.target.value })}
              placeholder="e.g. 123456"
              className="w-full border-2 border-[#E4E4E7] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]"
              data-testid="sovrn-site-id"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1">
              API Key {sovrn?.api_key_preview && <span className="text-[#A1A1AA] normal-case font-normal">(current: {sovrn.api_key_preview})</span>}
            </label>
            <input
              type="password"
              value={sovrnForm.api_key}
              onChange={(e) => setSovrnForm({ ...sovrnForm, api_key: e.target.value })}
              placeholder={sovrn?.api_key_preview ? 'Leave blank to keep current' : 'Paste API key'}
              className="w-full border-2 border-[#E4E4E7] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]"
              data-testid="sovrn-api-key"
            />
          </div>
        </div>

        <label className="inline-flex items-center gap-2 mb-4 cursor-pointer" data-testid="sovrn-active-label">
          <input
            type="checkbox"
            checked={sovrnForm.active}
            onChange={(e) => setSovrnForm({ ...sovrnForm, active: e.target.checked })}
            className="w-4 h-4 accent-[#FF3B30]"
            data-testid="sovrn-active-toggle"
          />
          <span className="text-sm font-bold uppercase tracking-wide">Active</span>
        </label>

        <div className="flex items-center gap-3">
          <button
            onClick={saveSovrn}
            disabled={sovrnSaving}
            className="inline-flex items-center gap-2 bg-[#FF3B30] text-white px-6 py-2.5 text-xs font-bold uppercase tracking-wide hover:bg-[#D62828] disabled:opacity-50 transition-colors"
            data-testid="sovrn-save-btn"
          >
            {sovrnSaving ? <CircleNotch size={14} className="animate-spin" /> : <Check size={14} />}
            Save Sovrn Config
          </button>
          {sovrnMsg && (
            <span className={`text-xs font-bold ${sovrnMsg.type === 'success' ? 'text-[#10B981]' : 'text-[#FF3B30]'}`} data-testid="sovrn-msg">
              {sovrnMsg.text}
            </span>
          )}
        </div>
      </div>

      {/* ============ Pending Approvals ============ */}
      <div className="border-2 border-[#0A0A0A] bg-white p-6" data-testid="pending-section">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-black uppercase tracking-tight">Pending Approvals</h2>
          <button
            onClick={loadPending}
            disabled={loadingPending}
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-[#52525B] hover:text-[#FF3B30] transition-colors disabled:opacity-50"
            data-testid="pending-refresh"
          >
            <ArrowsClockwise size={14} className={loadingPending ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
        <p className="text-sm text-[#52525B] mb-5">
          Stores you've applied to via an affiliate network and are awaiting
          the advertiser ID. Each one is hidden from the public site until
          you approve it here. Paste the ID from your network dashboard,
          click <strong>Approve</strong>, and it goes live instantly.
        </p>

        {loadingPending ? (
          <div className="flex items-center gap-2 text-[#52525B]"><CircleNotch size={18} className="animate-spin" /> Loading…</div>
        ) : pending.length === 0 ? (
          <p className="text-sm text-[#52525B] italic">No pending stores. All applied advertisers are wired up. 🎉</p>
        ) : (
          <div className="border border-[#E4E4E7] divide-y divide-[#E4E4E7]" data-testid="pending-list">
            {pending.map(p => (
              <div key={p.store_id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`pending-row-${p.store_id}`}>
                <div className="flex-1 min-w-[240px]">
                  <p className="font-bold text-sm">{p.name} <span className="text-[#A1A1AA] font-normal">({p.region})</span></p>
                  <p className="text-xs text-[#52525B] uppercase tracking-wide font-mono">{p.affiliate_network} · {p.store_id}</p>
                </div>
                <input
                  type="text"
                  value={advInputs[p.store_id] || ''}
                  onChange={(e) => setAdvInputs({ ...advInputs, [p.store_id]: e.target.value })}
                  placeholder="Advertiser ID"
                  className="border-2 border-[#E4E4E7] px-3 py-2 text-sm w-44 focus:outline-none focus:border-[#0A0A0A]"
                  data-testid={`pending-input-${p.store_id}`}
                />
                <button
                  onClick={() => approve(p.store_id)}
                  disabled={approving[p.store_id]}
                  className="inline-flex items-center gap-1 bg-[#10B981] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide hover:bg-[#0d9568] disabled:opacity-50 transition-colors"
                  data-testid={`pending-approve-${p.store_id}`}
                >
                  {approving[p.store_id] ? <CircleNotch size={12} className="animate-spin" /> : <Check size={12} />}
                  Approve & Activate
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-5 p-4 bg-[#FEF3C7] border-l-4 border-[#F59E0B] flex items-start gap-3" data-testid="pending-help">
          <Warning size={20} className="text-[#92400E] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#92400E]">
            <p className="font-bold mb-1">How this works</p>
            <p>Pre-mark a store as "applied" using
              <code className="bg-white px-2 py-0.5 mx-1 text-xs">POST /api/admin/affiliate-pending/bulk</code>
              with <code className="bg-white px-2 py-0.5 mx-1 text-xs">{"{network, store_ids}"}</code>.
              It will appear here, hidden from customers, until you approve it.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
